import { Icon, Typography } from '@repo/ui';
import c from 'classnames';
import { Card } from 'components';
import type { EventPropertiesMap } from 'contexts/analytics/types';
import {
  MARKED_IN_SURVEY_HOME_LABEL,
  MarkedInSurveyBadge,
} from '../MarkedInSurveyBadge';
import styles from './ConditionLinkCard.module.css';

type ConditionLinkCardProps<E extends keyof EventPropertiesMap> = {
  name: string;
  /** Goes to `Card`'s `clickable`, so a bare slug resolves against the current route. */
  href?: string;
  /** Renders the tinted "marked in survey" header band + highlighted border. */
  markedInSurvey?: boolean;
  /** Fired on tap, alongside the card's own navigation. */
  onClick?: () => void;
  /** Sent on tap in place of `Card`'s default `Clicked Button`. Needs `href`. */
  analyticsEventName?: E;
  analyticsProperties?: EventPropertiesMap[E];
  /** Between the name and the chevron. */
  trailing?: React.ReactNode;
  /** Below the name row. */
  children?: React.ReactNode;
  className?: string;
};

export default function ConditionLinkCard<
  E extends keyof EventPropertiesMap = 'Clicked Button',
>({
  name,
  href,
  markedInSurvey = false,
  onClick,
  analyticsEventName,
  analyticsProperties,
  trailing,
  className,
  children,
}: ConditionLinkCardProps<E>) {
  const clickableData = href
    ? ({
        ...(analyticsProperties ?? { text: '' }),
        href,
      } as EventPropertiesMap[E] & {
        href: string;
      })
    : undefined;

  return (
    <Card
      clickable={clickableData}
      analyticsEventName={analyticsEventName}
      onClick={onClick}
      className={c(
        styles.container,
        markedInSurvey && styles.markedCard,
        className,
      )}
      cardBodyClassName={c(
        markedInSurvey && styles.markedBody,
        styles.linkCardBody,
      )}
    >
      {markedInSurvey && (
        <div className={styles.markedHeader}>
          <MarkedInSurveyBadge label={MARKED_IN_SURVEY_HOME_LABEL} />
        </div>
      )}
      <div className={c(styles.card, markedInSurvey && styles.markedContent)}>
        <div className={styles.nameRow}>
          <Typography variant='body-s' className={styles.name}>
            {name}
          </Typography>
          <div className={styles.trailing}>
            {trailing}
            <Icon name='chevronForward' size='s' />
          </div>
        </div>
        {children}
      </div>
    </Card>
  );
}
